"use client";

import { ReactNode, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslations } from "@/i18n/useTranslations";
import {
  Settings,
  LogOut,
  KeyRound,
  Menu,
  PenLine,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { UserRole } from "@comp-sys/shared";
import { useLogout } from "@/hooks/use-logout";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useNavigation } from "@/hooks/use-navigation";
import { ChangeNameDialog } from "@/components/users/change-name-dialog";
import { ChangePasswordDialog } from "@/components/users/change-password-dialog";
import { useMessageStore } from "@/stores/message-store";

interface MobileSidebarProps {
  trigger?: ReactNode;
}

export function MobileSidebar({ trigger }: MobileSidebarProps) {
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const tUser = useTranslations("users");
  const tRole = useTranslations("user");
  const tAuth = useTranslations("auth");
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { logout, isPending } = useLogout();
  const { navItems } = useNavigation();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const [open, setOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Track which groups are expanded. Auto-expand when a child route is active.
  const autoExpandedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of navItems) {
      if (item.children?.some((child) =>
        pathname === child.href || pathname.startsWith(child.href + '/')
      )) {
        keys.add(item.href);
      }
    }
    return keys;
  }, [navItems, pathname]);

  const [manuallyToggled, setManuallyToggled] = useState<Set<string>>(new Set());

  const isGroupExpanded = (href: string) => {
    if (manuallyToggled.has(href) || manuallyToggled.has(href + ':open')) {
      return manuallyToggled.has(href + ':open');
    }
    return autoExpandedKeys.has(href);
  };

  const toggleGroup = (href: string) => {
    setManuallyToggled((prev) => {
      // Check manual state first (collapsed OR expanded), then fall back to auto-expand
      const hasManualState = prev.has(href) || prev.has(href + ':open');
      const isOpen = hasManualState ? prev.has(href + ':open') : autoExpandedKeys.has(href);
      const next = new Set(prev);
      next.delete(href);
      next.delete(href + ':open');
      if (isOpen) {
        next.add(href);
      } else {
        next.add(href + ':open');
      }
      return next;
    });
  };

  // Auto-close sidebar on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        className={cn(
          "inline-flex items-center justify-center rounded-lg p-2",
          "glass-button transition-all duration-200",
          "hover:bg-overlay/80",
          "focus:outline-none focus:ring-2 focus:ring-primary/20",
          !trigger && "h-10 w-10"
        )}
      >
        {trigger ?? (
          <>
            <Menu className="h-5 w-5" />
            <span className="sr-only">{tCommon("toggleMenu")}</span>
          </>
        )}
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-72 p-0 glass-card border-r border-surface"
        showCloseButton={false}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
        </SheetHeader>

        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border dark:border-border">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-accent" />
            <span className="font-bold text-lg">Comp-Sys</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => {
            const hasChildren = !!item.children?.length;
            const isActive = pathname === item.href || (item.href !== '/dashboard' && pathname.startsWith(item.href + '/'));
            const expanded = isGroupExpanded(item.href);

            return (
              <div key={item.href}>
                {hasChildren ? (
                  // Group header with expand/collapse toggle
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary-dark text-primary-foreground shadow-card shadow-primary/25"
                        : "text-muted-foreground hover:bg-overlay/50 hover:text-foreground"
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleGroup(item.href);
                    }}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium flex-1 min-w-0">{t(item.labelKey)}</span>
                    <span
                      className="p-0.5 rounded hover:bg-overlay/20"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleGroup(item.href);
                      }}
                    >
                      <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", !expanded && "-rotate-90")} />
                    </span>
                  </Link>
                ) : (
                  // Regular link
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary-dark text-primary-foreground shadow-card shadow-primary/25"
                        : "text-muted-foreground hover:bg-overlay/50 hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">{t(item.labelKey)}</span>
                    {item.href === "/dashboard/messages" && unreadCount > 0 && (
                      <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-destructive text-primary-foreground text-[11px] font-bold px-1.5">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                {/* Children: only visible when group is expanded */}
                {hasChildren && expanded && (
                  <div className="ml-6 space-y-1">
                    {item.children!.map((child) => {
                      const isChildActive = child.href === item.href
                        ? pathname === child.href
                        : pathname === child.href || pathname.startsWith(child.href + '/');
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-200",
                            isChildActive
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-muted-foreground hover:bg-overlay/30 hover:text-foreground"
                          )}
                        >
                          {child.icon && <child.icon className="h-4 w-4" />}
                          <span>{t(child.labelKey)}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Card */}
        <div className="border-t border-border dark:border-border p-2">
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                "flex items-center gap-3 w-full rounded-xl p-3",
                "hover:bg-muted/80",
                "transition-colors duration-200 cursor-pointer"
              )}
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src="/avatar.png" />
                <AvatarFallback>
                  {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || user?.phoneNumber?.[0]?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              {user && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">
                    {user.displayName || user.email || user.phoneNumber}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {tRole(`role.${user.activeRole}`)}
                  </p>
                </div>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-48">
              <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
                <PenLine className="h-4 w-4" />
                {tUser("changeName")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                <KeyRound className="h-4 w-4" />
                {tUser("changePassword")}
              </DropdownMenuItem>
              {(user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                    <Settings className="h-4 w-4" />
                    {tAuth("logout.settings")}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                disabled={isPending}
                className="text-error-text"
              >
                <LogOut className="h-4 w-4" />
                {tAuth("logout.title")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SheetContent>

      <ChangeNameDialog
        open={changeNameOpen}
        onOpenChange={setChangeNameOpen}
      />

      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </Sheet>
  );
}
